/**
 * EVD-177 — 90-Tage-Routine plan selector (smap PDP, unified-mini-abc-test-2 / -3).
 *
 * Loaded globally (same convention as multipack-selector.js) but a full no-op
 * unless a [data-routine-plan-selector] element exists on the page — that
 * only happens on templates that add the routine_plan_selector block.
 *
 * Does NOT reimplement pack selection or selling-plan resolution. Instead it
 * drives the two systems that already do that:
 *  - clicks the real pack-size radio ([data-variant-input]) that
 *    assets/multipack-selector.js listens to, so MultipackSelector applies
 *    the 3-pack exactly as if the shopper had clicked it themselves;
 *  - clicks the real Seal DOM (.sls-selling-plan-group-first-line /
 *    input.sls-option) for whichever Seal plan is present under the 3-pack's
 *    routine_selling_plan_ids in multipacks.configs (a dedicated Seal group,
 *    separate from the pack's regular selling_plan_ids used by every other
 *    template), so Seal remains the source of truth for what gets submitted.
 *
 * Neither the Seal plan id nor its interval name is hardcoded here — both
 * are read at runtime from multipacks.configs (via the sibling
 * [data-multipack-selector] element already rendered by
 * snippets/multipack-selector.liquid) and matched against Seal's own DOM.
 *
 * Load order requirement: this file must load AFTER assets/multipack-selector.js
 * (see layout/theme.liquid) — its own formdata safeguard below is meant to run
 * after, and take precedence over, MultipackSelector's own formdata safeguard.
 */
(function () {
  'use strict';

  var LABELS = {
    routine: 'Routine starten - 20% sparen',
    onetime: 'In den Einkaufswagen'
  };

  function toArray(nodeList) {
    return Array.prototype.slice.call(nodeList || []);
  }

  function whenReady(el, isReady, cb) {
    if (isReady()) { cb(); return; }
    var observer = new MutationObserver(function () {
      if (isReady()) {
        observer.disconnect();
        cb();
      }
    });
    observer.observe(el, { childList: true, subtree: true });
  }

  function RoutinePlanSelector(root) {
    this.root = root;
    this.productSection = root.closest('[data-section-type="product"]');
    if (!this.productSection) return;

    this.mpContainer = this.productSection.querySelector('[data-multipack-selector]');
    if (!this.mpContainer) return;

    try {
      this.packs = JSON.parse(this.mpContainer.dataset.packs);
    } catch (e) {
      return;
    }

    var quantity = parseInt(root.dataset.routinePackQuantity, 10) || 3;
    this.routinePack = (this.packs || []).find(function (p) { return p.quantity === quantity; });
    if (!this.routinePack || !this.routinePack.routine_selling_plan_ids) return;

    this.routinePlanIds = Object.keys(this.routinePack.routine_selling_plan_ids).map(
      function (key) { return String(this.routinePack.routine_selling_plan_ids[key]); }, this
    );
    if (!this.routinePlanIds.length) return;

    this.atcTextEl = this.productSection.querySelector('[data-add-to-cart-text]');

    this.lastOneTimePackRadio = null;
    this._pendingSealSelectTimeout = null;

    this._bindPlanRadios();
    this._registerFormSafeguard();
    this._registerGifts();

    var self = this;
    document.addEventListener('multipack:changed', function (e) {
      self._updateRoutinePriceDisplay();

      // The template's ab_test_variant_selector block force-selects its
      // default pack (6er) on load and again at 800ms / 1500ms (see
      // snippets/product-template.liquid), after Routine has already picked
      // the 3-pack. Pack cards are hidden while Routine is active, so any
      // pack change in that state is programmatic: re-apply the routine pack
      // (and its Seal plan, which applyPack resets to one-time).
      var qty = e.detail && e.detail.packQuantity;
      if (self.root.getAttribute('data-plan') !== 'routine') return;
      if (!qty || qty === self.routinePack.quantity) return;
      window.setTimeout(function () {
        if (self.root.getAttribute('data-plan') === 'routine') self.activateRoutine();
      }, 0);
    });

    // Routine is the default state on page load (see evd-177 briefing).
    this.activateRoutine();
  }

  RoutinePlanSelector.prototype._bindPlanRadios = function () {
    var self = this;
    toArray(this.root.querySelectorAll('input[name="ed-bb-plan"]')).forEach(function (input) {
      input.addEventListener('change', function () {
        if (input.value === 'routine') {
          self.activateRoutine();
        } else {
          self.activateOneTime();
        }
      });
    });
  };

  /**
   * Belt-and-suspenders correctness guard, independent of whether Seal's own
   * click/DOM has actually caught up by submit time (Seal is a third-party
   * app; its render timing is outside this repo's control). Registered
   * capture:true on the 'formdata' event, same mechanism
   * assets/multipack-selector.js#_registerFormSafeguards already uses, and
   * loaded after it (see layout/theme.liquid) so this listener runs LAST and
   * has final say: while the routine plan is active, force `selling_plan` to
   * one of the routine pack's own ids regardless of what Seal's DOM currently
   * shows.
   */
  RoutinePlanSelector.prototype._registerFormSafeguard = function () {
    var self = this;
    var form = this.productSection.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    form.addEventListener('formdata', function (e) {
      if (self.root.getAttribute('data-plan') !== 'routine') return;
      var current = String(e.formData.get('selling_plan') || '');
      var target = self.routinePlanIds.indexOf(current) !== -1 ? current : self.routinePlanIds[0];
      if (target) e.formData.set('selling_plan', String(target));
      var label = self._buildRoutineDisplayLabel();
      if (label) e.formData.set('properties[_ed_pack_display_label]', label);
    }, true);

    // The theme's AJAX add-to-cart (theme.utils.serialize in assets/theme.js)
    // reads input values directly and never fires 'formdata', so also pin the
    // selling_plan and display-label inputs themselves. Registered after
    // MultipackSelector's own submit/click handlers (load order), so this
    // runs last and overrides the 3-pack label _syncPackLineProperties wrote.
    var syncRoutineInputs = function () {
      if (self.root.getAttribute('data-plan') !== 'routine') return;
      var input = self.productSection.querySelector('input[name="selling_plan"]');
      if (input) {
        var current = String(input.value || '');
        if (self.routinePlanIds.indexOf(current) === -1) input.value = self.routinePlanIds[0];
      }
      var labelInput = self.productSection.querySelector('[data-multipack-pack-display-label]');
      var label = self._buildRoutineDisplayLabel();
      if (labelInput && label) labelInput.value = label;
    };
    form.addEventListener('submit', syncRoutineInputs, true);
    var atcBtn = this.productSection.querySelector('[data-add-to-cart]');
    if (atcBtn) atcBtn.addEventListener('click', syncRoutineInputs);
  };

  /**
   * Routine counterpart of MultipackSelector#_buildDisplayLabel, written to
   * _ed_pack_display_label (cart line + checkout subtitle extension) while
   * the Routine is active, e.g. "90-Tage-Routine (27,96 € / Stück) – 20%".
   * Without it the Routine line carries the regular 3-pack label of the
   * multipacks.configs entry it reuses. Same unit price as the panel teaser
   * (_updateRoutinePriceDisplay); plan name from the panel's own title.
   */
  RoutinePlanSelector.prototype._buildRoutineDisplayLabel = function () {
    if (!window.theme || !theme.Currency || !theme.settings) return '';

    var titleEl = this.root.querySelector('.ed-bb__plan--routine .ed-bb__plan-title');
    var name = titleEl ? titleEl.textContent.trim() : '';
    var basePrice = parseInt(this.mpContainer.dataset.basePrice, 10);
    if (!name || !basePrice || isNaN(basePrice)) return '';

    var discount = parseFloat(this.root.dataset.routineDiscountPercentage);
    if (isNaN(discount)) discount = 0;
    var pricePerUnit = Math.round(basePrice * (100 - discount) / 100);

    var flavourDropdown = this.productSection.querySelector('[data-flavour-dropdown]');
    var flavour = flavourDropdown ? flavourDropdown.value : '';

    return name
      + ' (' + theme.Currency.formatMoney(pricePerUnit, theme.settings.moneyFormat) + ' / Stück)'
      + (discount ? ' – ' + discount + '%' : '')
      + (flavour ? ' / ' + flavour : '');
  };

  /**
   * Shipping notes (custom.custom_tags, rendered by snippets/product-form.liquid
   * as .meta-custom-tags__tag[data-trigger]). product-form.liquid toggles them
   * from clicks on Seal's group labels, bound once after 1500ms — but Seal's
   * widget is hidden here and the Routine plan is often clicked before that
   * binding exists, and multipack-selector.js renames every subscription
   * group to "Wiederholte Lieferung", so a Seal group name can't be used as a
   * Routine-only trigger. Re-applied here on every plan change instead:
   *  - Routine: entries whose trigger is the panel title ("90-Tage-Routine");
   *    if there are none, the entries for the selected Seal group as before;
   *  - Einmalkauf: the entries for the selected Seal group, as before.
   * Entries without a trigger are never touched.
   */
  RoutinePlanSelector.prototype._syncShippingNotes = function () {
    var tags = toArray(this.productSection.querySelectorAll('.meta-custom-tags__tag[data-trigger]'));
    if (!tags.length) return;

    var matches = [];
    if (this.root.getAttribute('data-plan') === 'routine') {
      var titleEl = this.root.querySelector('.ed-bb__plan--routine .ed-bb__plan-title');
      var routineName = titleEl ? titleEl.textContent.trim() : '';
      matches = tags.filter(function (tag) { return routineName && tag.dataset.trigger === routineName; });
    }

    if (!matches.length) {
      var checked = this.productSection.querySelector('.sealsubs-container input.sls-option:checked');
      var container = checked && checked.closest('.sls-option-container');
      var nameEl = container && container.querySelector('.sls-selling-plan-group-name');
      var groupName = nameEl ? nameEl.textContent : '';
      matches = tags.filter(function (tag) { return groupName && groupName.indexOf(tag.dataset.trigger) !== -1; });
    }

    tags.forEach(function (tag) {
      tag.classList.toggle('hidden', matches.indexOf(tag) === -1);
    });
  };

  /**
   * Welcome gifts for the first Routine order. The gift variants come from
   * the routine_gift metaobjects' variant field (data-routine-gifts, see
   * snippets/routine-plan-selector.liquid) — the same entries the panel
   * displays. Registered as form.edGetExtraCartItems, which
   * ProductForm.addItemFromForm (assets/theme.js) calls at submit time and
   * sends in the SAME /cart/add.js request as the Routine line, so the cart
   * never holds one without the other.
   *
   * State is read at submit time, not earlier: the template's
   * ab_test_variant_selector block switches packs again at 800ms / 1500ms.
   * Gifts carry _ed_gift / _ed_gift_for (hidden in the cart, used by
   * assets/routine-gifts-cart.js) and no _ed_pack_* (so the multipack Cart
   * Transform leaves them alone). Checkout-side abuse (gift without Routine)
   * is blocked by the multipack app's Validation Function, not here.
   */
  RoutinePlanSelector.prototype._registerGifts = function () {
    var self = this;
    var form = this.productSection.querySelector('form[action*="/cart/add"]');
    if (!form) return;

    var gifts;
    try {
      gifts = JSON.parse(this.root.dataset.routineGifts || '[]');
    } catch (e) {
      return;
    }
    gifts = (gifts || []).filter(function (g) { return g && g.id && g.available; });
    if (!gifts.length) return;

    var productId = this.mpContainer.dataset.productId || '';

    form.edGetExtraCartItems = function () {
      if (self.root.getAttribute('data-plan') !== 'routine') return null;
      // Same value the request will carry (last selling_plan in the form,
      // see _postCartAddWithExtras in assets/theme.js).
      var plan = new URLSearchParams(theme.utils.serialize(form)).getAll('selling_plan').pop();
      if (!plan || self.routinePlanIds.indexOf(String(plan)) === -1) return null;

      // One gift set per cart: skip if an earlier Routine add already brought them.
      return window.fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(function (cart) {
          var hasGifts = (cart.items || []).some(function (item) {
            return item.properties && item.properties._ed_gift;
          });
          if (hasGifts) return null;
          return gifts.map(function (g) {
            return {
              id: g.id,
              quantity: 1,
              properties: { _ed_gift: 'routine', _ed_gift_for: String(productId) }
            };
          });
        });
    };
  };

  RoutinePlanSelector.prototype._findPackRadio = function (quantity) {
    var flavourDropdown = this.productSection.querySelector('[data-flavour-dropdown]');
    var flavour = flavourDropdown ? flavourDropdown.value : null;

    var inputs = toArray(
      this.productSection.querySelectorAll('input[data-variant-input][data-packaging-quantity="' + quantity + '"]')
    );
    if (flavour) {
      var byFlavour = inputs.filter(function (inp) {
        return (inp.getAttribute('data-variant-flavour') || '') === flavour;
      });
      if (byFlavour.length) inputs = byFlavour;
    }

    return inputs.find(function (inp) { return !inp.disabled && !inp.classList.contains('disabled'); })
      || inputs[0]
      || null;
  };

  /**
   * Mirrors assets/multipack-selector.js#_syncSubvariantRadioForPack: set
   * .checked directly and dispatch 'change' (this is the theme's own radio,
   * not a third-party app's, so a manual dispatch — not .click() — is the
   * established pattern here).
   *
   * Always dispatches, even if the radio was already checked (e.g. a
   * ?variant= deep link straight to the routine pack's own quantity, or that
   * pack already being product.selected_or_first_available_variant): this is
   * what routes through MultipackSelector.applyPack() ->
   * _resetSealToOneTime() -> _hideNonBaseGroups(), which is what actually
   * re-applies window.edRoutineAllowedSellingPlanIds. Without an
   * unconditional dispatch, activateRoutine()/activateOneTime() could update
   * that global without ever getting Seal's group visibility re-evaluated
   * against the new value.
   */
  RoutinePlanSelector.prototype._selectPackRadio = function (input) {
    if (!input) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  };

  /**
   * Find the .sls-option-container whose plan id(s) intersect the given
   * pack's routine_selling_plan_ids values — same matching strategy
   * _hideNonBaseGroups() in multipack-selector.js uses for the default pack.
   */
  RoutinePlanSelector.prototype._findSealContainerForPack = function (pack) {
    var sealContainer = this.productSection.querySelector('.sealsubs-container');
    if (!sealContainer || !pack || !pack.routine_selling_plan_ids) return null;

    var targetIds = Object.keys(pack.routine_selling_plan_ids).map(
      function (key) { return String(pack.routine_selling_plan_ids[key]); }
    );

    var containers = toArray(sealContainer.querySelectorAll('.sls-option-container'));
    for (var i = 0; i < containers.length; i++) {
      var c = containers[i];
      var radio = c.querySelector('input.sls-option');
      if (!radio || radio.dataset.type !== 'subscription') continue;

      var candidateIds = [];
      var freqSelect = c.querySelector('select[name="subs_interval"]');
      if (freqSelect) {
        toArray(freqSelect.options).forEach(function (opt) { candidateIds.push(String(opt.value)); });
      }
      var planInput = c.querySelector('input[name="selling_plan"]');
      if (planInput && planInput.value) candidateIds.push(String(planInput.value));
      if (radio.dataset.sellingPlanId) candidateIds.push(String(radio.dataset.sellingPlanId));

      if (candidateIds.some(function (id) { return targetIds.indexOf(id) !== -1; })) {
        return c;
      }
    }
    return null;
  };

  /**
   * Click the matched group's first-line (same mechanism
   * snippets/product-form.liquid's own sls-select builder and
   * _resetSealToOneTime() already use to drive Seal's own state). Guarded by
   * the container's own checked state, same convention as
   * _resetSealToOneTime()'s "!oneTimeRadio.checked" check, so a retry never
   * re-toggles an already-correct selection.
   */
  RoutinePlanSelector.prototype._clickSealContainer = function (container) {
    if (!container) return;
    var radio = container.querySelector('input.sls-option');
    if (radio && radio.checked) return;

    var firstLine = container.querySelector('.sls-selling-plan-group-first-line');
    if (firstLine) {
      firstLine.click();
    } else if (radio) {
      radio.click();
    }
  };

  /**
   * Verify the click actually landed (Seal's exact re-render timing isn't
   * something this repo controls) and retry a couple of times before giving
   * up quietly. Correctness at submit time does not depend on this having
   * succeeded — see _registerFormSafeguard above.
   */
  RoutinePlanSelector.prototype._ensureRoutinePlanSelected = function (attempt) {
    var self = this;
    attempt = attempt || 0;

    var sealContainer = this.productSection.querySelector('.sealsubs-container');
    var sellingPlanInput = sealContainer && sealContainer.querySelector('input[name="selling_plan"]');
    var isSelected = sellingPlanInput
      && this.routinePlanIds.indexOf(String(sellingPlanInput.value)) !== -1;

    if (isSelected || attempt >= 3) {
      this._syncShippingNotes();
      return;
    }

    this._clickSealContainer(this._findSealContainerForPack(this.routinePack));

    window.setTimeout(function () {
      self._ensureRoutinePlanSelected(attempt + 1);
    }, 200);
  };

  RoutinePlanSelector.prototype._selectSealRoutinePlan = function () {
    var self = this;
    if (this._pendingSealSelectTimeout) {
      window.clearTimeout(this._pendingSealSelectTimeout);
      this._pendingSealSelectTimeout = null;
    }

    whenReady(
      this.productSection,
      function () { return !!self._findSealContainerForPack(self.routinePack); },
      function () {
        // MultipackSelector.applyPack() (triggered by our own pack radio
        // click below) resets Seal to one-time synchronously AND re-applies
        // its group hiding again ~300ms later (see
        // assets/multipack-selector.js _resetSealToOneTime). Wait past that
        // before picking our plan, or the delayed re-apply can undo it. Not
        // load-bearing for correctness (see _registerFormSafeguard above) —
        // only affects how quickly the UI settles.
        self._pendingSealSelectTimeout = window.setTimeout(function () {
          self._pendingSealSelectTimeout = null;
          self._ensureRoutinePlanSelected(0);
        }, 400);
      }
    );
  };

  /**
   * Recompute the teaser price/billing text from the CURRENT resolved base
   * variant price (assets/multipack-selector.js keeps
   * [data-multipack-selector]'s data-base-price attribute in sync with the
   * true base variant, self-correcting for cases like a ?variant= deep link
   * or a flavour switch) rather than trusting the server-rendered value,
   * which reflects whatever current_variant happened to be at page render.
   */
  RoutinePlanSelector.prototype._updateRoutinePriceDisplay = function () {
    if (!window.theme || !theme.Currency || !theme.settings) return;

    var basePrice = parseInt(this.mpContainer.dataset.basePrice, 10);
    if (!basePrice || isNaN(basePrice)) return;

    var discount = parseFloat(this.root.dataset.routineDiscountPercentage);
    if (isNaN(discount)) discount = 0;
    var billingLabel = this.root.dataset.routineBillingLabel || '';

    var pricePerUnit = Math.round(basePrice * (100 - discount) / 100);
    var billingTotal = pricePerUnit * this.routinePack.quantity;

    var priceWas = this.root.querySelector('.ed-bb__price-was');
    var priceNow = this.root.querySelector('.ed-bb__price-now');
    var billing = this.root.querySelector('.ed-bb__billing');

    if (priceWas) priceWas.innerHTML = theme.Currency.formatMoney(basePrice, theme.settings.moneyFormat);
    if (priceNow) {
      // Template C: same routine price per pack, shown per day (see snippet).
      if (this.root.dataset.routinePriceDisplay === 'per_day') {
        var daysPerPack = parseInt(this.root.dataset.routineDaysPerPack, 10) || 30;
        priceNow.innerHTML = theme.Currency.formatMoney(Math.round(pricePerUnit / daysPerPack), theme.settings.moneyFormat) + ' / Tag';
      } else {
        priceNow.innerHTML = theme.Currency.formatMoney(pricePerUnit, theme.settings.moneyFormat) + ' / Packung';
      }
    }
    if (billing) {
      billing.innerHTML = 'Abrechnung '
        + theme.Currency.formatMoney(billingTotal, theme.settings.moneyFormat)
        + ' ' + billingLabel;
    }
  };

  RoutinePlanSelector.prototype._setUIState = function (plan) {
    document.documentElement.setAttribute('data-ed-bb-plan', plan);

    this.root.setAttribute('data-plan', plan);
    toArray(this.root.querySelectorAll('[data-plan-option]')).forEach(function (el) {
      var input = el.querySelector('input[name="ed-bb-plan"]');
      var isMatch = el.getAttribute('data-plan-option') === plan;
      el.classList.toggle('is-selected', isMatch);
      if (input) input.checked = isMatch;
    });

    if (this.atcTextEl) {
      // theme.js Product.prototype.updateCartButton may have just set a
      // sold-out/unavailable label off the pack-radio change we triggered —
      // dataset.defaultText must still track the active plan (so a later
      // re-enable restores the right label), but don't clobber a currently
      // disabled button's visible "Ausverkauft" text with our own label.
      var cartBtn = this.productSection.querySelector('[data-add-to-cart]');
      if (!cartBtn || !cartBtn.disabled) {
        this.atcTextEl.textContent = LABELS[plan];
      }
      this.atcTextEl.dataset.defaultText = LABELS[plan];
    }
  };

  RoutinePlanSelector.prototype.activateRoutine = function () {
    // Read fresh by multipack-selector.js#_hideNonBaseGroups on every Seal
    // (re-)hide pass; cleared in activateOneTime() below so "Einmalkauf"
    // reverts to the normal single-subscription-group behaviour.
    window.edRoutineAllowedSellingPlanIds = this.routinePlanIds;

    var checked = this.productSection.querySelector('input[data-variant-input]:checked');
    if (checked) this.lastOneTimePackRadio = checked;

    this._selectPackRadio(this._findPackRadio(this.routinePack.quantity));
    this._selectSealRoutinePlan();
    this._updateRoutinePriceDisplay();
    this._setUIState('routine');
  };

  RoutinePlanSelector.prototype.activateOneTime = function () {
    window.edRoutineAllowedSellingPlanIds = [];

    if (this._pendingSealSelectTimeout) {
      window.clearTimeout(this._pendingSealSelectTimeout);
      this._pendingSealSelectTimeout = null;
    }

    var sealContainer = this.productSection.querySelector('.sealsubs-container');
    var oneTimeRadio = sealContainer && sealContainer.querySelector('input.sls-option[data-type="one_time"]');
    if (oneTimeRadio && !oneTimeRadio.checked) oneTimeRadio.click();

    this._selectPackRadio(this.lastOneTimePackRadio || this._findPackRadio(1));
    this._setUIState('onetime');
    this._syncShippingNotes();
  };

  function init() {
    toArray(document.querySelectorAll('[data-routine-plan-selector]')).forEach(function (root) {
      new RoutinePlanSelector(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
